/**
 * SalesFunnels — Funnels Overview (CRM Phase 1 / Baustein A, #1511).
 * Read-only Reporting: pro Funnel (= leads.quelle) Stage-Breakdown + KPIs,
 * plus Gesamtübersicht zum Funnel-Vergleich. Schreibt keine Lead-Daten.
 *
 * @page /sales/funnels
 * @roles admin, manager
 */
import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Filter, Users, Trophy, XCircle, Clock } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function api(path) {
  return fetch(`${API}${path}`, { credentials: 'include' }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data;
  });
}

// Zentrale Stage-Config (Reihenfolge + Anzeige-Label via i18n + Balken-Farbe).
// preisanfrage..gewonnen = Funnel-Leiter, verloren terminal.
const STAGE_LABELS = {
  preisanfrage: 'sales.stages.preisanfrage',
  neu: 'sales.stages.neu',
  kontaktiert: 'sales.stages.kontaktiert',
  termin_vereinbart: 'sales.stages.termin_vereinbart',
  angebot_gesendet: 'sales.stages.angebot_gesendet',
  gewonnen: 'sales.stages.gewonnen',
  verloren: 'sales.stages.verloren',
};

const STAGE_COLORS = {
  preisanfrage: '#22D3EE',
  neu: '#338edc',
  kontaktiert: '#6366F1',
  termin_vereinbart: '#0891B2',
  angebot_gesendet: '#4D9CE0',
  gewonnen: '#16A34A',
  verloren: '#9CA3AF',
};

// Anzeige-Label für Funnel-Quellen (Fallback = Roh-Wert).
const QUELLE_LABELS = {
  website: 'sales.funnels.quelle.website',
  website_form: 'sales.funnels.quelle.website_form',
  manuell: 'sales.funnels.quelle.manuell',
  preiskalkulator: 'sales.funnels.quelle.preiskalkulator',
  unbekannt: 'sales.funnels.quelle.unbekannt',
};

function fmtDwell(seconds, t) {
  if (seconds == null) return '–';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const min = seconds / 60;
  if (min < 60) return `${Math.round(min)} min`;
  const std = min / 60;
  if (std < 48) return `${std.toFixed(1)} ${t('sales.funnels.hoursShort')}`;
  const tage = std / 24;
  return `${tage.toFixed(1)} ${t('sales.funnels.daysShort')}`;
}

function KpiCard({ label, value, sub, icon: Icon, iconColor }) {
  return (
    <div className="border border-[var(--border-card,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)] p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-1">{label}</p>
          <p className="text-2xl font-bold text-[var(--text-primary)]">{value}</p>
          {sub && <p className="text-sm text-[var(--text-muted)] mt-1">{sub}</p>}
        </div>
        {Icon && <Icon size={20} className={iconColor} />}
      </div>
    </div>
  );
}

function FunnelCard({ funnel, ladder, t }) {
  const qLabel = QUELLE_LABELS[funnel.quelle] ? t(QUELLE_LABELS[funnel.quelle]) : funnel.quelle;
  const stageMap = Object.fromEntries(funnel.stages.map((s) => [s.stage, s]));
  // Balken-Skala: max reached über die Leiter (oder total).
  const maxReached = Math.max(funnel.total, ...ladder.map((s) => stageMap[s]?.reached || 0), 1);
  const verloren = stageMap.verloren;

  return (
    <div className="border border-[var(--border-card,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)] p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-base font-semibold text-[var(--text-primary)]">{qLabel}</h3>
        <span className="text-xs text-[var(--text-tertiary)] font-mono">{funnel.quelle}</span>
      </div>

      {funnel.total === 0 ? (
        <p className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('sales.funnels.emptyFunnel')}</p>
      ) : (
        <>
          {/* KPI-Zeile pro Funnel — Werte IMMER --text-primary */}
          <div className="grid grid-cols-3 gap-3 my-4">
            <div>
              <p className="text-[11px] uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('sales.funnels.leads')}</p>
              <p className="text-xl font-bold text-[var(--text-primary)]">{funnel.total}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('sales.funnels.wonRate')}</p>
              <p className="text-xl font-bold text-[var(--text-primary)]">{funnel.won_rate}%</p>
              <p className="text-[11px] text-[var(--text-muted)]">{funnel.won} {t('sales.funnels.won')}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('sales.funnels.lostRate')}</p>
              <p className="text-xl font-bold text-[var(--text-primary)]">{funnel.lost_rate}%</p>
              <p className="text-[11px] text-[var(--text-muted)]">{funnel.lost} {t('sales.funnels.lost')}</p>
            </div>
          </div>

          {/* Funnel-Leiter als Balken — reached pro Stage */}
          <div className="space-y-2 mt-4">
            {ladder.map((stage) => {
              const s = stageMap[stage] || { reached: 0, conversion: 0, dwell_avg_seconds: null };
              const pct = Math.round((s.reached / maxReached) * 100);
              return (
                <div key={stage} className="flex items-center gap-3">
                  <div className="w-32 shrink-0 text-xs text-[var(--text-secondary)] text-right">{t(STAGE_LABELS[stage])}</div>
                  <div className="flex-1 h-6 rounded-md bg-[var(--bg-inset,#F8F9FC)] overflow-hidden">
                    <div
                      className="h-full rounded-md flex items-center px-2"
                      style={{ width: `${Math.max(pct, s.reached > 0 ? 4 : 0)}%`, backgroundColor: STAGE_COLORS[stage] }}
                    >
                      {s.reached > 0 && <span className="text-[11px] font-medium text-white">{s.reached}</span>}
                    </div>
                  </div>
                  <div className="w-14 shrink-0 text-xs text-[var(--text-primary)] text-right tabular-nums">{s.conversion}%</div>
                  <div className="w-20 shrink-0 text-xs text-[var(--text-tertiary)] text-right flex items-center justify-end gap-1">
                    <Clock size={12} /> {fmtDwell(s.dwell_avg_seconds, t)}
                  </div>
                </div>
              );
            })}
          </div>

          {verloren && verloren.reached > 0 && (
            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[var(--border-table,#F3F4F6)] text-xs text-[var(--text-secondary)]">
              <XCircle size={14} className="text-[#9CA3AF]" />
              {t('sales.stages.verloren')}: <span className="text-[var(--text-primary)] font-medium">{verloren.reached}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function SalesFunnels() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api('/api/sales/funnels-overview')
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <div className="h-8 w-48 rounded bg-[var(--bg-inset,#F8F9FC)] animate-pulse mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-24 rounded-xl bg-[var(--bg-inset,#F8F9FC)] animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="border rounded-xl p-4" style={{ borderColor: '#F59E0B', backgroundColor: '#FFFBEB' }}>
          <p className="text-sm text-amber-800">{t('sales.funnels.loadError')}: {error}</p>
        </div>
      </div>
    );
  }

  const { funnels = [], gesamt, ladder = [] } = data || {};
  const activeFunnels = funnels.filter((f) => f.total > 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('sales.funnels.title')}</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">{t('sales.funnels.subtitle')}</p>
      </div>

      {/* Gesamtübersicht — KPI-Leiste */}
      {gesamt && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label={t('sales.funnels.totalLeads')} value={gesamt.total} icon={Users} iconColor="text-[#338edc]" />
          <KpiCard label={t('sales.funnels.activeFunnels')} value={activeFunnels.length} icon={Filter} iconColor="text-[#6366F1]" />
          <KpiCard label={t('sales.funnels.wonRate')} value={`${gesamt.won_rate}%`} sub={`${gesamt.won} ${t('sales.funnels.won')}`} icon={Trophy} iconColor="text-[#16A34A]" />
          <KpiCard label={t('sales.funnels.lostRate')} value={`${gesamt.lost_rate}%`} sub={`${gesamt.lost} ${t('sales.funnels.lost')}`} icon={XCircle} iconColor="text-[#EF4444]" />
        </div>
      )}

      {/* Funnel-Vergleich Tabelle */}
      {activeFunnels.length > 0 && (
        <div className="border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('sales.funnels.funnel')}</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('sales.funnels.leads')}</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('sales.funnels.won')}</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('sales.funnels.wonRate')}</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('sales.funnels.lost')}</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('sales.funnels.lostRate')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-table,#F3F4F6)]">
              {activeFunnels.map((f) => (
                <tr key={f.quelle} className="hover:bg-[var(--bg-card-hover,#FAFAFE)]">
                  <td className="px-4 py-3 text-[var(--text-primary)] font-medium">{QUELLE_LABELS[f.quelle] ? t(QUELLE_LABELS[f.quelle]) : f.quelle}</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)] tabular-nums">{f.total}</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)] tabular-nums">{f.won}</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)] tabular-nums">{f.won_rate}%</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)] tabular-nums">{f.lost}</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)] tabular-nums">{f.lost_rate}%</td>
                </tr>
              ))}
              {gesamt && (
                <tr className="font-semibold bg-[var(--bg-inset,#F8F9FC)]">
                  <td className="px-4 py-3 text-[var(--text-primary)]">{t('sales.funnels.total')}</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)] tabular-nums">{gesamt.total}</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)] tabular-nums">{gesamt.won}</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)] tabular-nums">{gesamt.won_rate}%</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)] tabular-nums">{gesamt.lost}</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)] tabular-nums">{gesamt.lost_rate}%</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Spalten-Legende für die Funnel-Balken */}
      {activeFunnels.length > 0 && (
        <p className="text-xs text-[var(--text-tertiary)]">{t('sales.funnels.barLegend')}</p>
      )}

      {/* Pro-Funnel-Cards */}
      {activeFunnels.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center mb-4">
            <Filter size={28} className="text-[var(--text-muted)]" />
          </div>
          <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">{t('sales.funnels.emptyTitle')}</h3>
          <p className="text-sm text-[var(--text-tertiary)] max-w-sm">{t('sales.funnels.emptyDesc')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {activeFunnels.map((f) => (
            <FunnelCard key={f.quelle} funnel={f} ladder={ladder} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}
